/**
 * Mode Registry
 *
 * Central registry of all available training modes with metadata.
 * Used for navigation, UI display, and mode discovery.
 *
 * NOTE: "Simulatie" mode removed — merged with Assessment as "Toets" in Learning Lab (§3.4.12)
 */

import { ModeId, ModeMetadata } from './types'
import { Zap, Target, Trophy, Beaker } from 'lucide-react'

/**
 * Mode metadata registry
 */
export const MODE_REGISTRY: Record<ModeId, ModeMetadata> = {
  fluency: {
    id: 'fluency',
    title: 'Vloeiend Oefenen',
    subtitle: 'Automatiseren',
    description:
      'Train je snelheid en automatiseer basisvaardigheden met gerichte oefeningen.',
    icon: 'Zap',
    route: '/learning-lab/fluency',
    estimatedMinutes: 2,
    difficultyBand: 'Jouw niveau',
    tags: ['Snelheid', 'Automatiseren', 'Basis'],
    isEnabled: true,
  },
  sprint: {
    id: 'sprint',
    title: 'Focus Sprint',
    subtitle: 'Zwakke plekken trainen',
    description:
      'Focus op je zwakste onderwerpen met gerichte oefeningen en uitgebreide uitleg.',
    icon: 'Target',
    route: '/learning-lab/sprint',
    estimatedMinutes: 8,
    difficultyBand: 'Jouw zwakke punten',
    tags: ['Gericht', 'Zwakke punten', 'Uitleg'],
    isEnabled: true,
  },
  challenge: {
    id: 'challenge',
    title: 'Uitdaging',
    subtitle: 'Extra uitdaging',
    description:
      'Test jezelf met moeilijkere vragen uit onderwerpen die je al beheerst.',
    icon: 'Trophy',
    route: '/learning-lab/challenge',
    estimatedMinutes: 5,
    difficultyBand: 'Moeilijker',
    tags: ['Uitdaging', 'Gevorderd'],
    isEnabled: true,
  },
  experiments: {
    id: 'experiments',
    title: 'Experimenten',
    subtitle: 'Beta functies',
    description:
      'Probeer nieuwe trainingsmodi en functies die we aan het testen zijn.',
    icon: 'Beaker',
    route: '/learning-lab',
    estimatedMinutes: 0,
    difficultyBand: 'Variabel',
    tags: ['Beta', 'Experimenteel'],
    isEnabled: false,
    isBeta: true,
  },
}

/**
 * Get mode metadata by ID
 */
export function getModeMetadata(modeId: ModeId): ModeMetadata {
  return MODE_REGISTRY[modeId]
}

/**
 * Get all enabled modes
 */
export function getEnabledModes(): ModeMetadata[] {
  return Object.values(MODE_REGISTRY).filter((mode) => mode.isEnabled)
}

/**
 * Get icon component for mode
 */
export function getModeIcon(modeId: ModeId) {
  const iconMap = {
    fluency: Zap,
    sprint: Target,
    challenge: Trophy,
    experiments: Beaker,
  }
  return iconMap[modeId]
}
